import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Trash2, Save, Upload } from "lucide-react";

export default function GalleryManager() {
  const { t } = useAuth();
  const [galleries, setGalleries] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/cms/gallery").then(({ data }) => setGalleries(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (editing.id) await api.put(`/cms/gallery/${editing.id}`, editing);
    else await api.post("/cms/gallery", editing);
    setEditing(null); load();
  };

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    const { data } = await api.post("/cms/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setEditing({ ...editing, images: [...(editing.images || []), { url: data.url, caption: "" }] });
  };
  const remove = async (id) => { if (!window.confirm("Löschen?")) return; await api.delete(`/cms/gallery/${id}`); load(); };

  if (editing) {
    return (
      <div className="p-8 lg:p-12 mv-fade-in max-w-4xl">
        <div className="flex items-end justify-between mb-8">
          <h1 className="font-display text-4xl">{editing.id ? `${t("gallery")} bearbeiten` : `Neue ${t("gallery")}`}</h1>
          <div className="flex gap-2">
            <button onClick={() => setEditing(null)} className="mv-btn-ghost">Abbrechen</button>
            <button data-testid="save-gallery-btn" onClick={save} className="mv-btn"><Save className="w-4 h-4" /> Speichern</button>
          </div>
        </div>
        <div className="mv-card p-6 space-y-4">
          <div><label className="block text-xs mb-1">Titel</label><input className="mv-input" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></div>
          <div>
            <label className="block text-xs mb-1">Typ</label>
            <select className="mv-input" value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })}>
              <option value="salon">Salonbilder</option>
              <option value="before_after">Vorher / Nachher</option>
              <option value="team">Team</option>
              <option value="references">Referenzen</option>
            </select>
          </div>
          <div>
            <p className="mv-label mb-2">Bilder</p>
            <div className="grid grid-cols-3 gap-3 mb-3">
              {(editing.images || []).map((img, i) => (
                <div key={i} className="relative">
                  <img src={img.url} className="w-full h-32 object-cover rounded border" style={{ borderColor: "var(--mv-border)" }} />
                  <input className="mv-input text-xs mt-1" placeholder="Caption" value={img.caption || ""} onChange={(e) => { const a = [...editing.images]; a[i] = { ...img, caption: e.target.value }; setEditing({ ...editing, images: a }); }} />
                  <button onClick={() => setEditing({ ...editing, images: editing.images.filter((_, j) => j !== i) })} className="absolute top-1 right-1 p-1 bg-white rounded-full shadow"><Trash2 className="w-3 h-3 text-red-500" /></button>
                </div>
              ))}
            </div>
            <label className="mv-btn-ghost text-xs cursor-pointer"><Upload className="w-3 h-3" /> Bild hochladen<input data-testid="upload-gallery-image" type="file" accept="image/*" className="hidden" onChange={upload} /></label>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} /> Veröffentlichen</label>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 lg:p-12 mv-fade-in">
      <div className="flex items-end justify-between mb-10">
        <div><p className="mv-label mb-2">Bilder</p><h1 className="font-display text-4xl">{t("gallery")}</h1></div>
        <button data-testid="new-gallery-btn" onClick={() => setEditing({ title: "", type: "salon", images: [], published: true })} className="mv-btn"><Plus className="w-4 h-4" /> Neue Galerie</button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {galleries.map((g) => (
          <div key={g.id} onClick={() => setEditing(g)} className="mv-card p-5 cursor-pointer" data-testid={`gallery-card-${g.id}`}>
            <div className="grid grid-cols-3 gap-1 mb-3">
              {(g.images || []).slice(0, 3).map((img, i) => <img key={i} src={img.url} className="w-full h-16 object-cover rounded" />)}
            </div>
            <p className="text-xs mb-1" style={{ color: "var(--mv-secondary)" }}>{g.type}</p>
            <h3 className="font-display text-lg">{g.title}</h3>
            <p className="text-xs" style={{ color: "var(--mv-text-muted)" }}>{(g.images || []).length} Bilder</p>
            <button onClick={(e) => { e.stopPropagation(); remove(g.id); }} className="text-xs text-red-500 mt-2">Löschen</button>
          </div>
        ))}
      </div>
    </div>
  );
}
